import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { get } from '../api';
import { addDays, mondayOf, todayLocal } from '../dates';
import { needsAttention, type ScheduleVisit } from '../visitStatus';
import { AssignmentsIcon, DashboardIcon, MoneyIcon, MoreIcon, ScheduleIcon } from './NavIcons';

const TABS = [
  { to: '/', label: 'Dashboard', end: true, Icon: DashboardIcon },
  { to: '/schedule', label: 'Schedule', Icon: ScheduleIcon },
  { to: '/assignments', label: 'Assignments', Icon: AssignmentsIcon },
  { to: '/money', label: 'Money', Icon: MoneyIcon, within: ['/invoices/', '/bills/'] },
];

const isUnder = (path: string, to: string) => path === to || path.startsWith(`${to}/`);

/**
 * A phone's main navigation, under the thumb: the four places the office goes most, and More
 * for the rest of the menu (People, the Manage pages and the account), which opens the pane.
 */
export function TabBar(props: { menuOpen: boolean; onMore(): void }) {
  const { pathname } = useLocation();
  // This week's visits needing attention: the same count as the Schedule's own "Needs attention"
  // chip for the current week, so the badge and the page it opens agree. Refreshed as the office moves.
  const [attention, setAttention] = useState(0);
  useEffect(() => {
    let live = true;
    const monday = mondayOf(todayLocal());
    get<{ items: ScheduleVisit[] }>(`/api/schedule?from=${monday}&to=${addDays(monday, 6)}`)
      .then((d) => live && setAttention(d.items.filter(needsAttention).length))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [pathname]);

  const onTab = TABS.some((t) =>
    t.end
      ? pathname === t.to
      : isUnder(pathname, t.to) || t.within?.some((p) => pathname.startsWith(p)),
  );
  return (
    <nav className="tabbar" aria-label="Main pages">
      {TABS.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          className={({ isActive }) =>
            isActive || t.within?.some((p) => pathname.startsWith(p)) ? 'active' : undefined
          }
        >
          <span className="tab-icon">
            <t.Icon />
            {t.to === '/schedule' && attention > 0 && (
              <span className="tab-badge" aria-hidden="true">
                {attention > 99 ? '99+' : attention}
              </span>
            )}
          </span>
          <span>{t.label}</span>
          {t.to === '/schedule' && attention > 0 && (
            <span className="sr-only">, {attention} need attention this week</span>
          )}
        </NavLink>
      ))}
      <button
        type="button"
        className={!onTab || props.menuOpen ? 'tab-more active' : 'tab-more'}
        aria-expanded={props.menuOpen}
        aria-controls="nav-pane"
        onClick={props.onMore}
      >
        <span className="tab-icon">
          <MoreIcon />
        </span>
        <span>More</span>
      </button>
    </nav>
  );
}
